import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";

import type { EmailTemplate } from "@/models/clients";
import type { Quill } from "@vueup/vue-quill";

const attributesToRemove = [
  "data-denotation-char",
  "data-index",
  "data-name",
  "data-tag",
  "data-value",
  "data-background-color",
];

const cleanTag = (tag: Element) => {
  const shouldCleanTag =
    tag.tagName.toLowerCase() === "span" &&
    tag.hasAttribute("data-denotation-char");
  if (shouldCleanTag) {
    attributesToRemove.forEach((attr) => tag.removeAttribute(attr));
  }
};

export const removeDenotation = (denotationElement: Element) =>
  denotationElement.remove();

export const removeAttributesFromPayload = (htmlString: string | undefined) => {
  if (!htmlString) {
    return "";
  }
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  Array.from(doc.body.getElementsByTagName("*")).forEach((el) => {
    if (
      el.classList.contains("ql-mention-denotation-char") ||
      el.classList.contains("quill-tag-link-edit")
    ) {
      el.remove();
      return;
    }
    cleanTag(el);
  });

  return doc.body.innerHTML;
};

export const removeExtraPropertiesFromPayload = (
  payload: Partial<Omit<EmailTemplate, "user" | "client">> & {
    client_id?: string;
    dynamic_field_dropdown?: string;
    search?: string;
    [key: string]: unknown;
  }
) => {
  if (payload.dynamic_field_dropdown || payload.dynamic_field_dropdown === "") {
    delete payload.dynamic_field_dropdown;
  }

  if (payload.search || payload.search === "") {
    delete payload.search;
  }

  // Remove any search input fields
  Object.keys(payload).forEach((key) => {
    if (key.startsWith("search-")) {
      delete payload[key];
    }
  });
};

export const zeroWidthSpaceCharRegex = /[\u200B-\u200D\uFEFF]/g;
export const appTrackingTokenKey = "appTrackingToken=";

/**
 * Converts semantic HTML tags back to Quill's expected format
 * Used when loading existing semantic HTML into the editor
 */
export const convertSemanticToQuillHTML = (semanticHTML: string): string => {
  if (!semanticHTML) return "";

  // Convert semantic HTML tags back to Quill's expected format
  return (
    semanticHTML
      // Convert semantic tags to Quill size spans
      .replace(
        /<small>(.*?)<\/small>/g,
        '<span class="ql-size-small">$1</span>'
      )
      .replace(/<h3>(.*?)<\/h3>/g, '<span class="ql-size-large">$1</span>')
      .replace(/<h2>(.*?)<\/h2>/g, '<span class="ql-size-huge">$1</span>')
  );
};

/**
 * Converts Quill editor content to clean semantic HTML
 * Removes all Quill-specific classes and converts to proper semantic tags
 */
export const convertToPureHTML = (editorInstance: Quill | null): string => {
  if (!editorInstance) return "";

  try {
    // Get the delta (operations) from Quill
    const delta = editorInstance.getContents();
    // Configure the converter for clean semantic HTML
    const converter = new QuillDeltaToHtmlConverter(delta.ops, {
      inlineStyles: false,
      classPrefix: "",
      customTag: (format: string, op) => {
        // Convert Quill size formats to semantic HTML tags
        if (format === "size") {
          switch (op.attributes?.size) {
            case "small":
              return "small";
            case "large":
              return "h3";
            case "huge":
              return "h2";
            default:
              return "span";
          }
        }
        return undefined;
      },
    });

    const html = converter.convert().replace(/\s*class="[^"]*"/g, "");
    return html;
  } catch (error) {
    console.warn(
      "Failed to convert delta to HTML, falling back to innerHTML:",
      error
    );
    return editorInstance?.root.innerHTML || "";
  }
};

export const handleDenotationTags = () => {
  const denotationCharNodes = document.querySelectorAll(
    ".ql-mention-denotation-char"
  );
  if (denotationCharNodes.length) {
    denotationCharNodes.forEach((denotationCharNode) =>
      removeDenotation(denotationCharNode)
    );
  }
};
